import pako from 'pako'; // Import pako for GZIP compression
import toast from 'react-hot-toast';
import ApiClient from 'ApiClient';
import { useSelectedDesignStore } from 'context/SelectedDesignContext';
import { useDesignStore } from 'context/DesignContext';
import cv from "@techstark/opencv-js";
import zoneProcessor from 'components/Modals/PatientModal/utils/zoneProcessor';
import Line from 'components/Modals/PatientModal/utils/Line';

const api = new ApiClient();

export default class PressureData {
    constructor() {
        this.DeviceInfo = "TSoles";
        this.FrameTimes_Left = [];
        this.FrameTimes_Right = [];
        this.CalibFrames_Left = [];
        this.CalibFrames_Right = [];
        this.CalibMaxFrame_Left = new cv.Mat();
        this.CalibMaxFrame_Right = new cv.Mat();
        this.PressureSensorHeight = 7.76; // For payatek sensors
        this.PressureSensorWidth = 9.16; // For payatek sensors
        this.ExamType = "static";
        this.PressureMesurements = [];
        this.PressureCursors = [];
        this.ROI_Left = new cv.Rect();
        this.ROI_Right = new cv.Rect();
        this.COPs_Left = [];
        this.COPs_Right = [];
        this.Axis_Left = new Line();
        this.Axis_Right = new Line();
        this.MetLine_Left = new Line();
        this.MetLine_Right = new Line();
        this.WidthLine_Left = new Line();
        this.WidthLine_Right = new Line();
        this.HeelLine_Left = new Line();
        this.HeelLine_Right = new Line();
        this.Zones_Left = new zoneProcessor();
        this.Zones_Right = new zoneProcessor();
        this.FootIndex_Left = -1;
        this.FootIndex_Right = -1;
        this.MetCenter = [];
        this.HeelCenter = [];
        this.M1COP = [];
        this.M2COP = [];
        this.M3COP = [];
        this.M4COP = [];
        this.M5COP = [];
        this.M1P = [];
        this.M2P = [];
        this.M3P = [];
        this.M4P = [];
        this.M5P = [];
        this.LeftControlPoints = [];
        this.RightControlPoints = [];
        this.status = 'scanned';
        this.FootLength_Left = 248;
        this.FootLength_Right = 248;
        this.pressureDataList = null;
        this.leftSelectedTestsIndexes = [];
        this.rightSelectedTestsIndexes = [];
    }

    static cloneFrom(original) {
        const copy = new PressureData();

        copy.DeviceInfo = original.DeviceInfo;
        copy.FrameTimes_Left = original.FrameTimes_Left.slice();
        copy.FrameTimes_Right = original.FrameTimes_Right.slice();
        copy.CalibFrames_Left = original.CalibFrames_Left.slice();
        copy.CalibFrames_Right = original.CalibFrames_Right.slice();
        copy.PressureSensorHeight = original.PressureSensorHeight;
        copy.PressureSensorWidth = original.PressureSensorWidth;
        copy.ExamType = original.ExamType;
        copy.PressureMesurements = original.PressureMesurements.slice();
        copy.PressureCursors = original.PressureCursors.map(pc => pc.clone ? pc.clone() : pc);
        copy.COPs_Left = original.COPs_Left.slice();
        copy.COPs_Right = original.COPs_Right.slice();
        copy.MetCenter = original.MetCenter.slice();
        copy.HeelCenter = original.HeelCenter.slice();
        copy.M1COP = original.M1COP.slice();
        copy.M2COP = original.M2COP.slice();
        copy.M3COP = original.M3COP.slice();
        copy.M4COP = original.M4COP.slice();
        copy.M5COP = original.M5COP.slice();
        copy.M1P = original.M1P.slice();
        copy.M2P = original.M2P.slice();
        copy.M3P = original.M3P.slice();
        copy.M4P = original.M4P.slice();
        copy.M5P = original.M5P.slice();
        copy.LeftControlPoints = original.LeftControlPoints.slice();
        copy.RightControlPoints = original.RightControlPoints.slice();
        copy.FootIndex_Left = original.FootIndex_Left;
        copy.FootIndex_Right = original.FootIndex_Right;
        copy.status = original.status;

        copy.CalibMaxFrame_Left = original.CalibMaxFrame_Left.clone();
        copy.CalibMaxFrame_Right = original.CalibMaxFrame_Right.clone();
        copy.ROI_Left = new cv.Rect(original.ROI_Left.x, original.ROI_Left.y, original.ROI_Left.width, original.ROI_Left.height);
        copy.ROI_Right = new cv.Rect(original.ROI_Right.x, original.ROI_Right.y, original.ROI_Right.width, original.ROI_Right.height);

        copy.Axis_Left = Line.cloneFrom(original.Axis_Left);
        copy.Axis_Right = Line.cloneFrom(original.Axis_Right);
        copy.MetLine_Left = Line.cloneFrom(original.MetLine_Left);
        copy.MetLine_Right = Line.cloneFrom(original.MetLine_Right);
        copy.WidthLine_Left = Line.cloneFrom(original.WidthLine_Left);
        copy.WidthLine_Right = Line.cloneFrom(original.WidthLine_Right);
        copy.HeelLine_Left = Line.cloneFrom(original.HeelLine_Left);
        copy.HeelLine_Right = Line.cloneFrom(original.HeelLine_Right);
        copy.Zones_Left = zoneProcessor.cloneFrom(original.Zones_Left);
        copy.Zones_Right = zoneProcessor.cloneFrom(original.Zones_Right);
        copy.FootLength_Left = original.FootLength_Left;
        copy.FootLength_Right = original.FootLength_Right;

        copy.pressureDataList = original.pressureDataList;
        copy.leftSelectedTestsIndexes = original.leftSelectedTestsIndexes;
        copy.rightSelectedTestsIndexes = original.rightSelectedTestsIndexes;
        return copy;
    }

    static matToJSON(mat) {
        if (!mat) return null;

        const u16 = mat.data16U; // Assuming the input Mat is CV_16UC1
        const byteArray = new Uint8Array(u16.length * 2);
        const view = new DataView(byteArray.buffer);

        for (let i = 0; i < u16.length; i++) {
            view.setUint16(i * 2, u16[i], true); // Little-endian
        }

        // Convert byte array to base64 string
        const bytesBase64 = btoa(String.fromCharCode(...byteArray));

        return {
            Rows: mat.rows,
            Cols: mat.cols,
            NumberOfChannels: mat.channels(),
            CompressionRatio: 0,
            Bytes: bytesBase64,
        };
    }

    static jsonToMat(obj) {
        if (!obj) return null;
        if (obj instanceof cv.Mat) return obj;

        // Decode base64 to Uint8Array
        const binaryString = atob(obj.Bytes);
        const byteArray = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            byteArray[i] = binaryString.charCodeAt(i);
        }

        // Interpret bytes as 16-bit values
        const view = new DataView(byteArray.buffer);
        const u16 = new Uint16Array(obj.Rows * obj.Cols); // 1 channel assumed

        for (let i = 0; i < u16.length; i++) {
            u16[i] = view.getUint16(i * 2, true); // little-endian
        }

        const mat = new cv.Mat(obj.Rows, obj.Cols, cv.CV_16UC1);
        mat.data16U.set(u16);

        return mat;
    }

    toJSON() {
        return {
            DeviceInfo: this.DeviceInfo,
            PressureSensorWidth: this.PressureSensorWidth,
            PressureSensorHeight: this.PressureSensorHeight,
            FrameTimes_Left: this.FrameTimes_Left,
            FrameTimes_Right: this.FrameTimes_Right,
            CalibFrames_Left: (this.CalibFrames_Left || []).map(PressureData.matToJSON),
            CalibFrames_Right: (this.CalibFrames_Right || []).map(PressureData.matToJSON),
            CalibMaxFrame_Left: PressureData.matToJSON(this.CalibMaxFrame_Left),
            CalibMaxFrame_Right: PressureData.matToJSON(this.CalibMaxFrame_Right),
            ExamType: this.ExamType,
            PressureMesurements: this.PressureMesurements,
            PressureCursors: this.PressureCursors,
            ROI_Left: this.ROI_Left,
            ROI_Right: this.ROI_Right,
            COPs_Left: this.COPs_Left,
            COPs_Right: this.COPs_Right,
            Axis_Left: this.Axis_Left,
            Axis_Right: this.Axis_Right,
            MetLine_Left: this.MetLine_Left,
            MetLine_Right: this.MetLine_Right,
            WidthLine_Left: this.WidthLine_Left,
            WidthLine_Right: this.WidthLine_Right,
            HeelLine_Left: this.HeelLine_Left,
            HeelLine_Right: this.HeelLine_Right,
            Zones_Left: this.Zones_Left,
            Zones_Right: this.Zones_Right,
            FootIndex_Left: this.FootIndex_Left,
            FootIndex_Right: this.FootIndex_Right,
            MetCenter: this.MetCenter,
            HeelCenter: this.HeelCenter,
            M1COP: this.M1COP,
            M2COP: this.M2COP,
            M3COP: this.M3COP,
            M4COP: this.M4COP,
            M5COP: this.M5COP,
            M1P: this.M1P,
            M2P: this.M2P,
            M3P: this.M3P,
            M4P: this.M4P,
            M5P: this.M5P,
            LeftControlPoints: this.LeftControlPoints,
            RightControlPoints: this.RightControlPoints,
            status: this.status,
            FootLength_Left: this.FootLength_Left,
            FootLength_Right: this.FootLength_Right,
            leftSelectedTestsIndexes: this.leftSelectedTestsIndexes,
            rightSelectedTestsIndexes: this.rightSelectedTestsIndexes
        };
    }

    async Save() {
        const designId = useSelectedDesignStore.getState().designId;
        const hasPressure = useSelectedDesignStore.getState().hasPressure;

        try {
            let jsonString = '';
            if (this.pressureDataList?.length > 1) {
                if (this.LeftControlPoints.length != 0 && this.RightControlPoints.length != 0) this.CopytoFirst();

                jsonString = JSON.stringify(this.pressureDataList.map(pData => pData.toJSON()));
            } else
                jsonString = JSON.stringify(this.toJSON());

            const jsonBuffer = new TextEncoder().encode(jsonString);
            const compressedData = pako.gzip(jsonBuffer);
            const blob = new Blob([compressedData], { type: 'application/gzip' });

            const formData = new FormData();
            formData.append('file', blob);

            let response;

            if (!hasPressure)
                response = await api.post(`/api/designs/${designId}/pressure`, formData);
            else
                response = await api.put(`/api/designs/${designId}/pressure`, formData);

            if (!response.ok) {
                const msg = response.body?.message || response.body?.Message || `HTTP error! Status: ${response.status}`;
                throw new Error(msg);
            }

            if (response.status == 204) {
                useDesignStore.getState().setPressure(this);
                toast.success("Pressure data has been saved successfully.", { duration: 5000 });
            } else {
                toast.error("Could not save pressure data. Please try again.", { duration: 10000 });
                // await this.downloadJSON(jsonString); // fallback
            }

        } catch (error) {
            console.error('Error sending data to backend:', error);
            toast.error(error?.message || 'Could not save pressure data. Please try again.', { duration: 10000 });
            // await this.downloadJSON(JSON.stringify(this.toJSON())); // fallback
        }
    }

    downloadJSON(jsonString) {
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'pressureData.json';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    async Load() {
        try {
            const designId = useSelectedDesignStore.getState().designId;
            const responseFile = await api.get(`/api/designs/${designId}/pressure/file`);

            if (responseFile.ok === false)
                return false;

            const decompressedData = pako.ungzip(responseFile.body, { to: 'string' });
            const jsonObjmain = JSON.parse(decompressedData);

            if (jsonObjmain.constructor === Array) {
                this.pressureDataList = jsonObjmain.map(obj => Object.assign(new PressureData(), obj));
                this.pressureDataList.forEach(pd => {
                    pd.CalibFrames_Left = pd.CalibFrames_Left.map(PressureData.jsonToMat);
                    pd.CalibFrames_Right = pd.CalibFrames_Right.map(PressureData.jsonToMat);
                    pd.CalibMaxFrame_Left = pd.CalibMaxFrame_Left && pd.CalibMaxFrame_Left.Bytes !== '' ? PressureData.jsonToMat(pd.CalibMaxFrame_Left) : null;
                    pd.CalibMaxFrame_Right = pd.CalibMaxFrame_Right && pd.CalibMaxFrame_Right.Bytes !== '' ? PressureData.jsonToMat(pd.CalibMaxFrame_Right) : null;
                });
                this.SelectValidDynamicTests();
                this.getAverageDynamicPressureData();
            }

            const jsonObj = jsonObjmain.constructor === Array ? PressureData.cloneFrom(this.pressureDataList[0]) : jsonObjmain;

            this.DeviceInfo = jsonObj.DeviceInfo;
            this.PressureSensorWidth = jsonObj.PressureSensorWidth;
            this.PressureSensorHeight = jsonObj.PressureSensorHeight;
            this.FrameTimes_Left = jsonObj.FrameTimes_Left;
            this.FrameTimes_Right = jsonObj.FrameTimes_Right;
            this.CalibFrames_Left = (jsonObj.CalibFrames_Left || []).map(PressureData.jsonToMat);
            this.CalibFrames_Right = (jsonObj.CalibFrames_Right || []).map(PressureData.jsonToMat);
            this.CalibMaxFrame_Left = PressureData.jsonToMat(jsonObj.CalibMaxFrame_Left);
            this.CalibMaxFrame_Right = PressureData.jsonToMat(jsonObj.CalibMaxFrame_Right);
            this.ExamType = jsonObj.ExamType;
            this.PressureMesurements = jsonObj.PressureMesurements;
            this.PressureCursors = jsonObj.PressureCursors;
            this.ROI_Left = jsonObj.ROI_Left;
            this.ROI_Right = jsonObj.ROI_Right;
            this.COPs_Left = jsonObj.COPs_Left;
            this.COPs_Right = jsonObj.COPs_Right;
            this.Axis_Left = jsonObj.Axis_Left;
            this.Axis_Right = jsonObj.Axis_Right;
            this.MetLine_Left = jsonObj.MetLine_Left;
            this.MetLine_Right = jsonObj.MetLine_Right;
            this.WidthLine_Left = jsonObj.WidthLine_Left;
            this.WidthLine_Right = jsonObj.WidthLine_Right;
            this.HeelLine_Left = jsonObj.HeelLine_Left;
            this.HeelLine_Right = jsonObj.HeelLine_Right;
            this.Zones_Left = Object.assign(new zoneProcessor(), jsonObj.Zones_Left);
            this.Zones_Right = Object.assign(new zoneProcessor(), jsonObj.Zones_Right);
            this.FootIndex_Left = jsonObj.FootIndex_Left;
            this.FootIndex_Right = jsonObj.FootIndex_Right;
            this.MetCenter = jsonObj.MetCenter;
            this.HeelCenter = jsonObj.HeelCenter;
            this.M1COP = jsonObj.M1COP;
            this.M2COP = jsonObj.M2COP;
            this.M3COP = jsonObj.M3COP;
            this.M4COP = jsonObj.M4COP;
            this.M5COP = jsonObj.M5COP;
            this.M1P = jsonObj.M1P;
            this.M2P = jsonObj.M2P;
            this.M3P = jsonObj.M3P;
            this.M4P = jsonObj.M4P;
            this.M5P = jsonObj.M5P;
            this.LeftControlPoints = jsonObj.LeftControlPoints;
            this.RightControlPoints = jsonObj.RightControlPoints;
            this.status = jsonObj.status == undefined ? 'scanned' : jsonObj.status;
            this.FootLength_Left = jsonObj.FootLength_Left;
            this.FootLength_Right = jsonObj.FootLength_Right;

            useSelectedDesignStore.setState({ hasPressure: true });
            useDesignStore.getState().setPressure(this);

            return true;
        }
        catch (exp) {
            console.error("Error loading pressure data:", exp);
            return false;
        }
    }

    areMatsEqual(mat1, mat2) {
        if (!mat1 || !mat2) return false;

        // Compare shape and type
        if (
            mat1.rows !== mat2.rows ||
            mat1.cols !== mat2.cols ||
            mat1.type() !== mat2.type()
        ) {
            return false;
        }

        return mat1.data16U.every((val, i) => val === mat2.data16U[i]);
    }

    SelectValidDynamicTests() {
        const leftCOPs = [];
        const rightCOPs = [];
        for (const pd of this.pressureDataList) {
            const copsLeft = pd.CalibFrames_Left.map(this.ComputeCOP);
            if (copsLeft.length != 0) {
                leftCOPs.push(copsLeft);
                pd.COPs_Left = copsLeft.slice();
            }

            const copsRight = pd.CalibFrames_Right.map(this.ComputeCOP);
            if (copsRight.length != 0) {
                rightCOPs.push(copsRight);
                pd.COPs_Right = copsRight.slice();
            }
        }

        const leftAverageCOP = this.GetAverageCOP(leftCOPs);
        const rightAverageCOP = this.GetAverageCOP(rightCOPs);

        this.leftSelectedTestsIndexes = this.SelectBestCOPsIndexes(leftCOPs, leftAverageCOP);
        this.rightSelectedTestsIndexes = this.SelectBestCOPsIndexes(rightCOPs, rightAverageCOP);
    }

    GetAverageCOP(copList) {
        const numberOfFrames = copList[0].length;
        const numberOfCOPs = copList.length;

        const averageCop = new Array(numberOfFrames).fill(0).map(() => ({ x: 0, y: 0 }));

        for (let i = 0; i < copList.length; i++) {
            for (let j = 0; j < numberOfFrames; j++) {
                averageCop[j].x += copList[i][j].x;
            }
        }

        for (let i = 0; i < numberOfFrames; i++) {
            averageCop[i].x /= numberOfCOPs;
            averageCop[i].y /= numberOfCOPs;
        }

        return averageCop;
    }

    SelectBestCOPsIndexes(COPs, averageCOP) {
        let selectedCOPsIndexes = [];
        const numberOfCOPs = COPs.length;

        if (numberOfCOPs === 1) {
            selectedCOPsIndexes.push(0);
        } else {
            const numberOfFrames = averageCOP.length;
            const numberOfSelectedCOPs = Math.floor(numberOfCOPs / 2);

            const copDiffWithAvg = [];

            for (let i = 0; i < numberOfCOPs; i++) {
                copDiffWithAvg[i] = [];
                for (let j = 0; j < numberOfFrames; j++) {
                    copDiffWithAvg[i][j] = {
                        x: Math.abs(COPs[i][j].x - averageCOP[j].x),
                        y: Math.abs(COPs[i][j].y - averageCOP[j].y)
                    };
                }
            }

            const COPsDiffs = new Array(numberOfCOPs);

            for (let i = 0; i < numberOfCOPs; i++) {
                let xTotalDiff = 0;
                let yTotalDiff = 0;

                for (let j = 0; j < numberOfFrames; j++) {
                    xTotalDiff += copDiffWithAvg[i][j].x;
                    yTotalDiff += copDiffWithAvg[i][j].y;
                }

                COPsDiffs[i] = xTotalDiff + yTotalDiff;
            }

            selectedCOPsIndexes = this.GetLowestValueIndices(COPsDiffs, numberOfSelectedCOPs);
        }
        return selectedCOPsIndexes;
    }

    GetLowestValueIndices(COPsDiffs, numberOfSelectedCOPs) {
        const indexedValues = COPsDiffs
            .map((value, index) => ({ Value: value, Index: index }));

        indexedValues.sort((a, b) => a.Value - b.Value);

        return indexedValues
            .slice(0, numberOfSelectedCOPs)
            .map(item => item.Index);
    }

    ComputeCOP(frame) {
        let sum = 0;
        let sumWY = 0;
        let sumWX = 0;
        if (frame === undefined) return new cv.Point(-1, -1);

        var fw = frame.cols;
        var fh = frame.rows;
        var fx = 0;
        var fy = 0;

        for (let x = fx; x < fw; x++) {
            for (let y = fy; y < fh; y++) {
                const fdata = frame.ucharPtr(y, x)[0];
                if (fdata > 4) {
                    sumWY += (fdata * y);
                    sumWX += (fdata * x);
                    sum += fdata;
                }
            }
        }
        if (sum === 0) {
            return new cv.Point((fw - fx) / 2, (fh - fy) / 2);
        }
        else {
            return new cv.Point(sumWX / sum, sumWY / sum);
        }
    }

    CopytoFirst() {
        if (!this.pressureDataList || this.pressureDataList.length === 0) return;

        const first = this.pressureDataList[0];
        const exclude = [
            'CalibFrames_Left',
            'CalibFrames_Right',
            'CalibMaxFrame_Left',
            'CalibMaxFrame_Right',
            'COPs_Left',
            'COPs_Right'
        ];

        Object.keys(this).forEach(key => {
            if (
                key !== 'pressureDataList' &&
                !exclude.includes(key) &&
                Object.prototype.hasOwnProperty.call(first, key)
            ) {
                first[key] = this[key];
            }
        });
    }

    getAverageDynamicPressureData() {
        if (!this.pressureDataList) return;

        if (this.leftSelectedTestsIndexes.length > 1) {
            const numOfLeftFrames = this.pressureDataList[0].CalibFrames_Left.length;
            const numOfLeftFootTests = this.leftSelectedTestsIndexes.length;
            const leftFrameWidth = this.pressureDataList[0].CalibFrames_Left[0].cols;
            const leftFrameHeight = this.pressureDataList[0].CalibFrames_Left[0].rows;

            const averageFramesLeft = [];
            const averageMaxLeft = new cv.Mat(leftFrameHeight, leftFrameWidth, cv.CV_16UC1, new cv.Scalar(0));

            for (let i = 0; i < numOfLeftFrames; i++) {
                const nullFrame = new cv.Mat(leftFrameHeight, leftFrameWidth, cv.CV_16UC1, new cv.Scalar(0));
                averageFramesLeft.push(nullFrame);
            }

            for (let i = 0; i < numOfLeftFootTests; i++) {
                const pd = this.pressureDataList[this.leftSelectedTestsIndexes[i]];

                for (let j = 0; j < pd.CalibFrames_Left.length; j++) {
                    cv.add(averageFramesLeft[j], pd.CalibFrames_Left[j], averageFramesLeft[j]);
                }
                cv.add(averageMaxLeft, pd.CalibMaxFrame_Left, averageMaxLeft);
            }

            const divisorLeft = new cv.Mat(leftFrameHeight, leftFrameWidth, cv.CV_16UC1, new cv.Scalar(numOfLeftFootTests));

            for (let i = 0; i < averageFramesLeft.length; i++) {
                cv.divide(averageFramesLeft[i], divisorLeft, averageFramesLeft[i]);
            }
            cv.divide(averageMaxLeft, divisorLeft, averageMaxLeft);

            this.CalibFrames_Left = averageFramesLeft.slice();
            this.CalibMaxFrame_Left = averageMaxLeft.clone();
        }
        else {
            this.CalibFrames_Left = this.pressureDataList[this.leftSelectedTestsIndexes[0]].CalibFrames_Left.slice();
            this.CalibMaxFrame_Left = this.pressureDataList[this.leftSelectedTestsIndexes[0]].CalibMaxFrame_Left.clone();
        }

        if (this.rightSelectedTestsIndexes.length > 1) {
            const numOfRightFrames = this.pressureDataList[0].CalibFrames_Right.length;
            const numOfRightFootTests = this.rightSelectedTestsIndexes.length;
            const rightFrameWidth = this.pressureDataList[0].CalibFrames_Right[0].cols;
            const rightFrameHeight = this.pressureDataList[0].CalibFrames_Right[0].rows;

            const averageFramesRight = [];
            const averageMaxRight = new cv.Mat(rightFrameHeight, rightFrameWidth, cv.CV_16UC1, new cv.Scalar(0));

            for (let i = 0; i < numOfRightFrames; i++) {
                const nullFrame = new cv.Mat(rightFrameHeight, rightFrameWidth, cv.CV_16UC1, new cv.Scalar(0));
                averageFramesRight.push(nullFrame);
            }

            for (let i = 0; i < numOfRightFootTests; i++) {
                const pd = this.pressureDataList[this.rightSelectedTestsIndexes[i]];

                if (pd.CalibFrames_Right.length > 0 && this.rightSelectedTestsIndexes.includes(i)) {
                    for (let j = 0; j < pd.CalibFrames_Right.length; j++) {
                        cv.add(averageFramesRight[j], pd.CalibFrames_Right[j], averageFramesRight[j]);
                    }
                    cv.add(averageMaxRight, pd.CalibMaxFrame_Right, averageMaxRight);
                }
            }

            const divisorRight = new cv.Mat(rightFrameHeight, rightFrameWidth, cv.CV_16UC1, new cv.Scalar(numOfRightFootTests));

            for (let i = 0; i < averageFramesRight.length; i++) {
                cv.divide(averageFramesRight[i], divisorRight, averageFramesRight[i]);
            }
            cv.divide(averageMaxRight, divisorRight, averageMaxRight);

            this.CalibFrames_Right = averageFramesRight.slice();
            this.CalibMaxFrame_Right = averageMaxRight.clone();
        }
        else {
            this.CalibFrames_Right = this.pressureDataList[this.rightSelectedTestsIndexes[0]].CalibFrames_Right.slice();
            this.CalibMaxFrame_Right = this.pressureDataList[this.rightSelectedTestsIndexes[0]].CalibMaxFrame_Right.clone();
        }
        this.status = 'scanned';
    }

}